/* eslint-disable object-shorthand */
import { CellValue, RowObject } from 'handsontable/common';
import { isObject } from 'lodash';
import sortBy from 'lodash/sortBy';

import { HandsontableCellType } from 'SharedDir/constants/field-types';
import {
  DynamicTableParams,
  FixedTableData,
  FixedTableParams,
  TableColumnType,
} from 'SharedDir/declarations/fields';

import HeaderCell from './_Table/header-cell';
import TableColumn from './_Table/table-column';

/**
 * Формируем массив с вложенными заголовками для HotTable
 *
 * **ВНИМАНИЕ!!!**
 *
 * Данный метод работает только с таблицами с фиксированными строками
 * @param params
 * @returns
 */
export const getFixedTableNestedHeaders = (
  params: FixedTableParams
): Array<Array<string | HeaderCell>> => {
  const headerRowsCount = params.cols.headers.length;
  return params.cols.headers.map((headerRow, rowIndex) => [
    rowIndex === headerRowsCount - 1 ? params.firstColumnHeader : '',
    ...headerRow,
  ]);
};

/**
 * Формируем массив с вложенными заголовками для HotTable
 *
 * **ВНИМАНИЕ!!!**
 *
 * Данный метод работает только с таблицами с динамическими строками
 * @param params
 * @returns
 */
export const getDynamicTableNestedHeaders = (
  params: DynamicTableParams
): Array<Array<string | HeaderCell>> => params.cols.headers.map((headerRow) => [...headerRow]);

/**
 * Формируем пустой массив с данными для HotTable
 *
 * **ВНИМАНИЕ!!!**
 *
 * Данный метод работает только с таблицами с фиксированными строками
 * @param params
 * @returns
 */
export const getFixedTableEmptyData = (params: FixedTableParams): FixedTableData => {
  const columnCells = Object.fromEntries(params.cols.columns.map((column) => [column.slug, null]));
  const data = params.rows
    .filter(({ slug }) => slug)
    .map((row, $index) => ({
      firstColumn: row.label,
      $index,
      $rowSlug: row.slug,
      ...columnCells,
    }));
  return data;
};

/**
 * Набор использования хуков handsobtabla'а для реализации "резиновых"
 * заголовков. Переносит содержимое заголовков на несколько строк средствами css.
 * И автоматически назначает соответствующую высоту каждой строке заголовка.
 *
 * Используемые хуки:
 * - [afterInit](https://handsontable.com/docs/12.2/javascript-data-grid/api/hooks/#afterinit);
 * - [afterGetColHeader](https://handsontable.com/docs/12.2/javascript-data-grid/api/hooks/#aftergetcolheader);
 *
 * Я даже не буду пытаться описать логику работы этого костыля, потому что она опирается
 * на логику отрисовки заголовков и таблиц самого хэндса.
 * А там без бутылки и на пальцах объяснить не получится(коротко, рамках коммента).
 *
 */
export const headerHeightFixer = {
  customHeaderHeight: [] as Array<number>,
  // eslint-disable-next-line func-names
  afterInit: function () {
    this.customHeaderHeight = [];
  },
  // eslint-disable-next-line func-names
  afterGetColHeader: function (row, TH: HTMLElement) {
    TH.style.whiteSpace = 'pre-wrap';
    // TH.style.wordBreak = 'break-all';
    // Должна работать нормально, в отличии от word-break
    TH.style.overflowWrap = 'break-word';
    TH.style.height = '';
    if (row === -1) {
      const next = TH.nextSibling as HTMLElement | null;
      const parent = TH.parentElement as HTMLElement;
      const headerIndex = Array.from(parent.parentNode?.children || []).indexOf(parent);
      if (next) {
        const height = next.clientHeight;
        parent.style.height = `${height}px`;
        this.customHeaderHeight[headerIndex] = height;
      } else {
        // +1 нужен видать из-за границ
        let height = this.customHeaderHeight[headerIndex];
        if (headerIndex === 0) {
          height += 1;
        }
        parent.style.height = `${height}px`;
      }
    } else {
      const element = TH.querySelector('.relative') as HTMLElement;
      if (element) {
        element.style.height = '100%';
      }
    }
  },
};

/**
 * Подготовка элементов выпадающего списка для типа ячейки _Значение каталога_
 *
 * @param optionsToPrepare
 */
export function prepareOptions(optionsToPrepare?: RowObject | CellValue[]): object[] {
  const preparedOptions = [] as any[];
  if (Array.isArray(optionsToPrepare)) {
    for (let i = 0, len = optionsToPrepare.length; i < len; i++) {
      if (!isObject(optionsToPrepare[i]) || !optionsToPrepare[i]) {
        throw new Error('selectOptions elements must be an object');
      }
      preparedOptions.push(optionsToPrepare[i]);
    }
    return preparedOptions;
  }

  throw new Error('selectOptions must be an array');
}
